import { useMemo, useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { isEqual } from 'lodash-es';
import { ExceptionTypes, ExceptionUtils } from '@frontend/api-schedule-exceptions';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Table,
  TableColumnConfig,
  Text,
  TableFilters,
  useForm,
  TimeRangeField,
  DateRangeField,
  CheckboxField,
  TextField,
  ModalControlModalProps,
} from '@frontend/design-system';

interface Props {
  exceptionData?: ExceptionTypes.ExceptionData[];
  handleEdit: (id: string) => void;
  handleDelete: (id: string) => void;
  filters: ExceptionTypes.ExceptionTableFilters;
  handleFiltersUpdate: (newFilters: ExceptionTypes.ExceptionTableFilters) => void;
}

export function ScheduleExceptionsTable({
  exceptionData,
  handleEdit,
  handleDelete,
  filters,
  handleFiltersUpdate,
}: Props) {
  const { t } = useTranslation('schedule');
  const { convertTimeForComparison, getDisplayDate, defaultFilterObject } = ExceptionUtils;
  const [searchValue, setSearchValue] = useState<string>('');
  const [filteredData, setFilteredData] = useState<ExceptionTypes.ExceptionData[]>();

  useEffect(() => {
    const data = exceptionData?.filter((item) => item?.name?.toLowerCase().includes(searchValue.toLowerCase())) || [];
    setFilteredData(data);
  }, [searchValue, exceptionData]);

  const filterByName = (exceptions: ExceptionTypes.ExceptionData[]) => {
    return exceptions?.filter((item) => item?.name?.toLowerCase().includes(filters.name?.toLowerCase() || '')) || [];
  };

  const filerByAvailability = (exceptions: ExceptionTypes.ExceptionData[]) => {
    if (!filters.isAvailable && !filters.isUnavailable) {
      return exceptions;
    }
    if (filters.isAvailable && filters.isUnavailable) {
      return exceptions;
    }
    if (filters.isAvailable && !filters.isUnavailable) {
      return exceptions?.filter((item) => item?.isAvailable.includes(filters.isAvailable.toString())) || [];
    }
    if (filters.isUnavailable && !filters.isAvailable) {
      return exceptions?.filter((item) => item?.isAvailable === 'false') || [];
    }
    return;
  };

  const filterByTime = (exceptions: ExceptionTypes.ExceptionData[]) => {
    if (filters.isAllDay) {
      return exceptions?.filter((item) => item?.startTime === '12:00am' && item.endTime === '11:59pm') || [];
    }
    if (filters.timeRange[0].length && !filters.isAllDay) {
      return (
        exceptions?.filter(
          (item) =>
            convertTimeForComparison(item?.startTime) >= convertTimeForComparison(filters.timeRange[0]) &&
            convertTimeForComparison(item?.endTime) <= convertTimeForComparison(filters.timeRange[1])
        ) || []
      );
    } else {
      return exceptions;
    }
  };

  useEffect(() => {
    let result = exceptionData;
    result = filerByAvailability(result || []);
    result = filterByName(result || []);
    result = filterByTime(result);
    setFilteredData(result);
  }, [filters]);

  const tableColumns = useMemo(
    (): TableColumnConfig<ExceptionTypes.ExceptionData>[] => [
      {
        Header: t('Name'),
        id: 'name',
        accessor: (row) => <Text as='span'>{row.name}</Text>,
        disableSortBy: true,
        minWidth: 100,
      },
      {
        Header: t('Availability'),
        id: 'available',
        accessor: (row) => {
          if (row.isAvailable === 'true') {
            return (
              <Text as='span' weight='semibold' color='success'>
                {t('Available')}
              </Text>
            );
          } else {
            return (
              <Text as='span' color='light'>
                {t('Unavailable')}
              </Text>
            );
          }
        },
        disableSortBy: true,
        width: 150,
        minWidth: 100,
      },
      {
        Header: t('Date'),
        id: 'date',
        accessor: (row) => (
          <Text as='span' weight='semibold'>
            {getDisplayDate(row.startDate, row.endDate)}
          </Text>
        ),
        disableSortBy: true,
      },
      {
        Header: t('Time'),
        id: 'time',
        accessor: (row) => {
          return row.startTime === '12:00am' && row.endTime === '11:59pm' ? (
            <Text as='span' weight='regular'>
              {t('All day')}
            </Text>
          ) : (
            <Text as='span' weight='regular'>
              {row.startTime} - {row.endTime}
            </Text>
          );
        },
        disableSortBy: true,
      },
    ],
    [exceptionData, filteredData]
  );

  const rowActionMenu = [
    {
      Icon: () => <Icon name='edit' />,
      label: t('Edit'),
      onClick: (row: ExceptionTypes.ExceptionData) => handleEdit(row.id),
    },
    {
      Icon: () => <Icon name='trash' />,
      label: t('Delete'),
      onClick: (row: ExceptionTypes.ExceptionData) => handleDelete(row.id),
    },
  ];

  return (
    <Table
      hasResponsiveColWidths
      hasGlobalSearch
      isPaginated
      colConfig={tableColumns}
      emptyStateConfig={{
        type: 'schedule',
        header: t('No exception data to display'),
      }}
      data={filteredData ?? []}
      rowActions={{
        actions: rowActionMenu,
      }}
      globalSearchConfig={{
        searchHandler: setSearchValue,
        placeholder: t('Search by Name'),
        debounceDelay: 500,
      }}
      manualFilters
      manualSortBy
      manualFiltersRender={(modalProps, setShowNotification) => (
        <ExceptionsTableFilters
          modalProps={modalProps}
          setShowNotificationBadge={setShowNotification}
          defaultFilters={defaultFilterObject}
          filters={filters}
          handleFiltersUpdate={handleFiltersUpdate}
        />
      )}
    />
  );
}

type ExceptionsTableFiltersProps = {
  modalProps: ModalControlModalProps;
  handleFiltersUpdate: (filters: ExceptionTypes.ExceptionTableFilters) => void;
  setShowNotificationBadge: (showNotification: boolean) => void;
  defaultFilters: ExceptionTypes.ExceptionTableFilters;
  filters: ExceptionTypes.ExceptionTableFilters;
};

const ExceptionsTableFilters = ({
  modalProps,
  defaultFilters,
  filters,
  setShowNotificationBadge,
  handleFiltersUpdate,
}: ExceptionsTableFiltersProps) => {
  const { t } = useTranslation('schedule');

  const { getFieldProps, seedValues, values, formProps } = useForm({
    fields: {
      name: { type: 'text', value: filters.name },
      isAvailable: { type: 'checkbox', value: filters.isAvailable },
      isUnavailable: { type: 'checkbox', value: filters.isUnavailable },
      dateRange: {
        type: 'dateRange',
        value: filters.dateRange,
      },
      timeRange: {
        type: 'timeRange',
        value: filters.timeRange,
        interval: 15,
      },
      isAllDay: { type: 'checkbox', value: filters.isAllDay },
    },
    onSubmit: (values) => {
      handleFiltersUpdate(values as ExceptionTypes.ExceptionTableFilters);
    },
  });

  return (
    <TableFilters
      {...modalProps}
      formProps={formProps}
      onApplyClick={() => {
        handleFiltersUpdate(values as ExceptionTypes.ExceptionTableFilters);
        setShowNotificationBadge(!isEqual(defaultFilters, values));
      }}
      onClearAllClick={() => {
        seedValues(defaultFilters);
        handleFiltersUpdate(defaultFilters);
        setShowNotificationBadge(false);
      }}
    >
      <TableFilters.Section sectionHeaderLabel={t('Name of Exception')}>
        <TextField {...getFieldProps('name')} label={t('Name of Exception')} />
      </TableFilters.Section>
      <TableFilters.Section sectionHeaderLabel={t('Select Availability')}>
        <CheckboxField
          {...getFieldProps('isUnavailable')}
          label={t('Unavailable')}
          css={css`
            margin-bottom: ${theme.spacing(2)};
          `}
        />
        <CheckboxField {...getFieldProps('isAvailable')} label={t('Available')} />
      </TableFilters.Section>
      <TableFilters.Section sectionHeaderLabel={t('Select Date Range')}>
        <DateRangeField {...getFieldProps('dateRange')} label={t('Start and end dates')}></DateRangeField>
      </TableFilters.Section>
      <TableFilters.Section sectionHeaderLabel={t('Select Time Range')}>
        <CheckboxField
          {...getFieldProps('isAllDay')}
          label={t('All Day')}
          css={css`
            margin-bottom: ${theme.spacing(2)};
          `}
        />
        {!getFieldProps('isAllDay').value && (
          <TimeRangeField {...getFieldProps('timeRange')} label={t('Start and end times')}></TimeRangeField>
        )}
      </TableFilters.Section>
    </TableFilters>
  );
};
